import { fetchBuildInfo } from "@/services/releaseNotesService.js";

// sessionStorage key for a /SupportCases deep link opened while signed out.
export const PENDING_SUPPORT_LINK_KEY = "vozcare_pending_support_link";

// Shared constants + helpers for the customer-facing Support Cases feature.
// Labels / colours must stay identical to docs/support_cases_api.md.

export const SUPPORT_STATUS = {
  OPEN: { label: "Open", fg: "#1D4ED8", bg: "#DBEAFE", icon: "mdi-circle-outline" },
  IN_PROGRESS: { label: "In progress", fg: "#B45309", bg: "#FEF3C7", icon: "mdi-progress-wrench" },
  PENDING_CUSTOMER: { label: "Awaiting your reply", fg: "#7C3AED", bg: "#EDE9FE", icon: "mdi-account-clock-outline" },
  RESOLVED: { label: "Resolved", fg: "#047857", bg: "#D1FAE5", icon: "mdi-check-circle-outline" },
  REOPENED: { label: "Reopened", fg: "#BE123C", bg: "#FFE4E6", icon: "mdi-restore" },
};

export const SUPPORT_SEVERITY = {
  LOW: { label: "Low", help: "General question or guidance", fg: "#475569", bg: "#F1F5F9", icon: "mdi-information-outline" },
  NORMAL: { label: "Normal", help: "Something isn't working as expected", fg: "#1D4ED8", bg: "#DBEAFE", icon: "mdi-alert-circle-outline" },
  HIGH: { label: "High", help: "My work is impacted", fg: "#B45309", bg: "#FEF3C7", icon: "mdi-alert-outline" },
  URGENT: { label: "Urgent", help: "My work is blocked / production down", fg: "#B91C1C", bg: "#FEE2E2", icon: "mdi-alert-octagon-outline" },
};

export const SUPPORT_SEVERITY_ORDER = ["LOW", "NORMAL", "HIGH", "URGENT"];

export const SUPPORT_MODULES = [
  "Dashboard",
  "Call Metrics",
  "Metrics Overview",
  "Live Monitoring",
  "Campaigns",
  "Clients",
  "Product Master",
  "Lead Data / CRM Data",
  "Lists",
  "Agents",
  "Admins",
  "Users",
  "Teams",
  "Roles",
  "Locations",
  "Disposition Codes",
  "Field Repository",
  "Call Recordings",
  "Reports & Exports",
  "Modification Requests",
  "Audit Logs",
  "Settings",
  "Login / Access",
  "Other",
];

// Only routes whose module is unambiguous.
const ROUTE_MODULE_MAP = {
  Dashboard: "Dashboard",
  CallMetrics: "Call Metrics",
  MetricsOverview: "Metrics Overview",
  Campaigns: "Campaigns",
  Clients: "Clients",
  ProductMaster: "Product Master",
  Lists: "Lists",
  Agents: "Agents",
  Admins: "Admins",
  Users: "Users",
  Teams: "Teams",
  Roles: "Roles",
  Locations: "Locations",
  DispositionCodes: "Disposition Codes",
  FieldRepository: "Field Repository",
  CallRecordings: "Call Recordings",
  Reports: "Reports & Exports",
  DataModificationRequests: "Modification Requests",
  AuditLogs: "Audit Logs",
  Settings: "Settings",
};

export function moduleForRoute(routeName) {
  return ROUTE_MODULE_MAP[routeName] || null;
}

export const SUPPORT_ALLOWED_EXTENSIONS = {
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  webp: "image/webp",
  pdf: "application/pdf",
  txt: "text/plain",
  csv: "text/csv",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  xls: "application/vnd.ms-excel",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  mp4: "video/mp4",
  webm: "video/webm",
};

export const SUPPORT_ACCEPT_ATTR = Object.keys(SUPPORT_ALLOWED_EXTENSIONS)
  .map((ext) => "." + ext)
  .join(",");

export const SUPPORT_MAX_FILE_BYTES = 15 * 1024 * 1024;
export const SUPPORT_MAX_FILES = 10;

export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function fileExtension(name) {
  const idx = (name || "").lastIndexOf(".");
  return idx >= 0 ? name.slice(idx + 1).toLowerCase() : "";
}

export function contentTypeFor(file) {
  const ext = fileExtension(file.name);
  return file.type || SUPPORT_ALLOWED_EXTENSIONS[ext] || "application/octet-stream";
}

// Returns an error string, or null when the file is acceptable.
export function validateSupportFile(file) {
  const ext = fileExtension(file.name);
  if (!SUPPORT_ALLOWED_EXTENSIONS[ext]) {
    return `"${file.name}" is not a supported file type`;
  }
  if (!file.size) return `"${file.name}" is empty`;
  if (file.size > SUPPORT_MAX_FILE_BYTES) {
    return `"${file.name}" is larger than 15 MB`;
  }
  return null;
}

export function isImageName(name) {
  return ["png", "jpg", "jpeg", "gif", "webp"].includes(fileExtension(name));
}

export function fileIconFor(name) {
  const ext = fileExtension(name);
  if (isImageName(name)) return "mdi-file-image-outline";
  if (ext === "pdf") return "mdi-file-pdf-box";
  if (["xlsx", "xls", "csv"].includes(ext)) return "mdi-file-excel-outline";
  if (ext === "docx") return "mdi-file-word-outline";
  if (["mp4", "webm"].includes(ext)) return "mdi-file-video-outline";
  return "mdi-file-document-outline";
}

export function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function formatDateTime(ms) {
  if (!ms) return "";
  return new Date(Number(ms)).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function relativeTime(ms) {
  if (!ms) return "";
  const diff = Date.now() - Number(ms);
  const sec = Math.round(diff / 1000);
  if (sec < 45) return "just now";
  const min = Math.round(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr} hr${hr === 1 ? "" : "s"} ago`;
  const day = Math.round(hr / 24);
  if (day < 7) return `${day} day${day === 1 ? "" : "s"} ago`;
  return new Date(Number(ms)).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

// Builds a File from a Blob (screenshot / pasted image) with a sensible name.
export function blobToFile(blob, baseName) {
  const type = blob.type || "image/png";
  const ext = type.split("/")[1] === "jpeg" ? "jpg" : type.split("/")[1] || "png";
  const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
  return new File([blob], `${baseName}-${stamp}.${ext}`, { type });
}

// Validates and appends files to an attachment item list (in place).
// Returns the list of user-readable rejection messages.
export function appendSupportFiles(list, files, { isScreenshot = false } = {}) {
  const errors = [];
  for (const file of Array.from(files || [])) {
    if (list.length >= SUPPORT_MAX_FILES) {
      errors.push(`You can attach up to ${SUPPORT_MAX_FILES} files per message`);
      break;
    }
    const err = validateSupportFile(file);
    if (err) {
      errors.push(err);
      continue;
    }
    list.push({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      file,
      is_screenshot: isScreenshot,
      progress: 0,
      status: "pending",
      key: null,
    });
  }
  return errors;
}

// Returns image Files found in a ClipboardEvent.
export function imagesFromClipboard(event) {
  const items = Array.from(event?.clipboardData?.items || []);
  return items
    .filter((it) => it.kind === "file" && it.type.startsWith("image/"))
    .map((it) => it.getAsFile())
    .filter(Boolean)
    .map((file) => (file.name && file.name !== "image.png" ? file : blobToFile(file, "pasted-image")));
}

// Captures the current page as a PNG File. Elements with the class
// "support-capture-exclude" (the support menu/dialog itself) are skipped.
// Returns null when capture fails - callers just continue without it.
// Only what is on screen is copied: html-to-image copies every element and its
// styles, and on Call Recordings (250 rows of buttons and icons) copying the
// rows scrolled out of view froze the page while a case was being raised
// (2026-10-28). Those rows are not in the picture anyway.
// VZC-100097 / VZC-100098: an off-screen table ROW itself is kept (only what
// is inside it is skipped). The copy keeps the table body's full height, so
// dropping rows stretched the visible ones until their text slid out of view
// and the picture showed an empty table. An empty row shell costs nothing.
const isOffScreen = (node) => {
  if (!(node instanceof Element)) return false;
  if (node.tagName === "TR") return false;
  const r = node.getBoundingClientRect();
  if (!r.width && !r.height) return false;
  return r.bottom < 0 || r.right < 0 || r.top > window.innerHeight || r.left > window.innerWidth;
};

export async function captureCurrentPage() {
  try {
    const { toBlob } = await import(/* webpackChunkName: "html-to-image" */ "html-to-image");
    const root = document.querySelector(".v-application") || document.body;
    const blob = await toBlob(root, {
      pixelRatio: 1,
      filter: (node) =>
        !(node.classList && node.classList.contains("support-capture-exclude")) && !isOffScreen(node),
    });
    return blob ? blobToFile(blob, "screenshot") : null;
  } catch (error) {
    console.warn("[support] page capture failed", error);
    return null;
  }
}

let buildVersionPromise = null;
export function getAppBuildVersion() {
  if (!buildVersionPromise) {
    buildVersionPromise = fetchBuildInfo()
      .then((info) => [info?.version, info?.commit].filter(Boolean).join(" ") || "unknown")
      .catch(() => {
        buildVersionPromise = null;
        return "unknown";
      });
  }
  return buildVersionPromise;
}
